import React, { useMemo } from 'react';
import { UserProfile, ClubEvent, Attendance, RankRequirement } from '../types';
import { db, handleFirestoreError, OperationType } from '../lib/firebase';
import { updateDoc, doc } from 'firebase/firestore';
import { calculatePromotionStats, getEligibleRank } from '../lib/promotion';
import { isZavalaExpired } from '../lib/utils';
import { Trophy, ArrowUpCircle, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';

interface PromotionBoardProps {
  users: UserProfile[];
  events: ClubEvent[];
  attendance: Attendance[];
  ranks: RankRequirement[];
}

export default function PromotionBoard({ users, events, attendance, ranks }: PromotionBoardProps) {
  const eligibleMembers = useMemo(() => {
    return users.filter(member => {
      if (
        member.status === 'inactive' || 
        isZavalaExpired(member.zavalaExpireDate) || 
        member.roles?.includes('guest') || 
        member.isGuest || 
        (member.shipName && !member.shipName.toLowerCase().includes('zavala'))
      ) return false;
      const stats = calculatePromotionStats(member, events, attendance);
      const eligibleRank = getEligibleRank(member, stats, ranks);
      const currentRankIndex = ranks.findIndex(r => r.id === member.currentRank || (member.currentRank === 'CDR' && r.id === 'CMDR'));
      const eligibleRankIndex = ranks.findIndex(r => r.id === eligibleRank.id);
      return eligibleRankIndex > currentRankIndex;
    }).map(member => {
      const stats = calculatePromotionStats(member, events, attendance);
      const eligibleRank = getEligibleRank(member, stats, ranks);
      return { member, stats, eligibleRank };
    });
  }, [users, events, attendance, ranks]);

  const handlePromote = async (member: UserProfile, newRankId: string) => {
    try {
      await updateDoc(doc(db, 'users', member.uid), {
        currentRank: newRankId,
        promotionRankDate: new Date().toISOString().split('T')[0]
      });
      toast.success(`Personnel promoted to ${newRankId}`);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'users');
    }
  };

  return (
    <div className="bg-[var(--background)] border-l-8 border-lcars-pink pl-4 lg:pl-6 py-2">
      <div className="flex justify-between items-center mb-6">
        <span className="text-xl font-bold text-lcars-pink uppercase">Promotion Review Board</span>
        <span className="text-[0.7rem] text-[var(--foreground)]/40 font-bold tracking-widest uppercase">
          {eligibleMembers.length} Candidates Pending Review
        </span>
      </div>

      {eligibleMembers.length > 0 ? (
        <div className="grid grid-cols-1 gap-4">
          {eligibleMembers.map(({ member, stats, eligibleRank }) => (
            <div 
              key={member.uid} 
              className="bg-lcars-pink/5 border-l-4 border-lcars-pink p-6 rounded-r-full flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 hover:bg-lcars-pink/10 transition-all"
            >
              <div className="space-y-1">
                <h3 className="text-2xl font-bold text-[var(--foreground)]">{member.firstName} {member.lastName}</h3>
                <div className="flex items-center gap-3">
                  <span className="bg-lcars-blue text-black px-3 py-0.5 rounded-full font-bold text-xs uppercase">
                    Current: {member.currentRank}
                  </span>
                  <ArrowUpCircle className="w-5 h-5 text-lcars-pink" />
                  <span className="bg-lcars-pink text-black px-3 py-0.5 rounded-full font-bold text-xs uppercase">
                    Eligible: {eligibleRank.shortName}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-6 flex-1 max-w-2xl">
                <div className="space-y-1">
                  <p className="text-[0.6rem] text-[var(--foreground)]/40 font-bold tracking-widest uppercase">Total Points</p>
                  <div className="flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-lcars-gold" />
                    <span className="text-xl font-bold">{stats.totalPoints}</span>
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-[0.6rem] text-[var(--foreground)]/40 font-bold tracking-widest uppercase">Time in Grade</p>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-lcars-blue" />
                    <span className="text-xl font-bold">{stats.monthsSinceLastPromotion}m</span>
                  </div>
                </div>
                <div className="space-y-1 hidden md:block">
                  <p className="text-[0.6rem] text-[var(--foreground)]/40 font-bold tracking-widest uppercase">Exams</p>
                  <div className="flex gap-2">
                    {member.ots && <CheckCircle2 className="w-5 h-5 text-lcars-tan" title="OTS Passed" />}
                    {member.occ && <CheckCircle2 className="w-5 h-5 text-lcars-blue" title="OCC Passed" />}
                  </div>
                </div>
              </div>

              <button 
                onClick={() => handlePromote(member, eligibleRank.id)}
                className="lcars-pill bg-lcars-pink text-black hover:bg-lcars-pink/80 w-full lg:w-auto px-8 py-3 text-lg shadow-[0_0_15px_rgba(204,102,153,0.3)]"
              >
                APPROVE PROMOTION
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 bg-white/5 rounded-r-full border-l-4 border-white/20">
          <p className="text-[var(--foreground)]/40 font-bold tracking-[0.3em] uppercase italic">
            No personnel currently eligible for promotion review.
          </p>
        </div>
      )}
    </div>
  );
}
